import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import CheckboxCard from 'shell/components/CheckboxCard';
import CheckboxCardGroup from 'shell/components/CheckboxCardGroup';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import {
    ASSIGNMENT_SUBMISSION_TYPES,
    getAssignmentSubmissionTypes,
    toggleAssignmentSubmissionType,
    updateEvidencePreferences,
} from 'utils/assignmentSubmissionTypes';
import EvidencePreferencesDialog from './EvidencePreferencesDialog';
import EVIDENCE_TYPE_DETAILS from './evidenceTypeDetails';

const SubmissionTypeSettings = ({ form, setForm, disabled = false }) => {
    const { t } = useTranslation('assignmentSettings');
    const [dialogType, setDialogType] = useState(null);
    const selected = getAssignmentSubmissionTypes(form);

    const handleTypeChange = (type) => {
        if (disabled) return;
        setForm((current) => toggleAssignmentSubmissionType(current, type));
    };

    const handleConfigure = (type) => {
        if (!disabled) setDialogType(type);
    };

    return (
        <Stack>
            <CheckboxCardGroup
                label={t('evidence.heading', 'What Will Students Submit? (Multi-Select)')}
                description={t(
                    'evidence.description',
                    'This decides what the AI can look at, and what you are asked to configure below. Syncs with the accepted file types on the assignment.'
                )}
                descriptionSx={{ mt: 5 }}
                gridSx={{ mt: 5 }}
            >
                {ASSIGNMENT_SUBMISSION_TYPES.map((type) => {
                    const config = EVIDENCE_TYPE_DETAILS[type];
                    const label = t(`evidence.types.${type}.label`, config.label);
                    const checked = selected.includes(type);
                    return (
                        <Box key={type} sx={{ position: 'relative', minWidth: 0 }}>
                            <CheckboxCard
                                checked={checked}
                                disabled={disabled}
                                icon={
                                    <Icon
                                        kind={config.icon}
                                        sx={{
                                            fontSize: '1.5rem',
                                            color: (theme) => theme.palette.ensightful.neutral[700],
                                        }}
                                    />
                                }
                                label={label}
                                labelSx={checked ? { pr: 9 } : undefined}
                                description={t(`evidence.types.${type}.description`, config.description)}
                                onChange={() => handleTypeChange(type)}
                            />
                            {checked && (
                                <IconButton
                                    aria-label={t('evidence.configure', 'Configure evidence for {{type}}', {
                                        type: label,
                                    })}
                                    kind={'settings'}
                                    size={'medium'}
                                    variant={'square'}
                                    disabled={disabled}
                                    sx={{
                                        position: 'absolute',
                                        right: (theme) => theme.spacing(14),
                                        top: (theme) => theme.spacing(3.5),
                                        color: (theme) =>
                                            disabled
                                                ? theme.palette.ensightful.neutral[400]
                                                : theme.palette.ensightful.primary[500],
                                        '&:hover': {
                                            color: (theme) => theme.palette.ensightful.primary[700],
                                            background: (theme) => theme.palette.ensightful.primary[100],
                                        },
                                        '&:active': {
                                            color: (theme) => theme.palette.ensightful.primary[800],
                                            background: (theme) => theme.palette.ensightful.primary[100],
                                        },
                                        '&.Mui-disabled': {
                                            color: (theme) => theme.palette.ensightful.neutral[400],
                                            background: 'transparent',
                                        },
                                    }}
                                    onClick={() => handleConfigure(type)}
                                />
                            )}
                        </Box>
                    );
                })}
            </CheckboxCardGroup>
            {dialogType && (
                <EvidencePreferencesDialog
                    type={dialogType}
                    preferences={form.gradingSettings?.evidence_preferences?.[dialogType]}
                    disabled={disabled}
                    onClose={() => setDialogType(null)}
                    onSave={(preferences) => {
                        if (disabled) return;
                        setForm((current) => updateEvidencePreferences(current, dialogType, preferences));
                        setDialogType(null);
                    }}
                />
            )}
        </Stack>
    );
};

export default SubmissionTypeSettings;
